import { createAsyncThunk } from '@reduxjs/toolkit';
import PaymentHooks from '../../utils/PaymentHooks';
import {
  EFTPOS_STATUS_UPDATE,
  FAILURE_REASON,
  PAYMENT_METHOD,
} from '../../constants';
import Logger from '../../utils/Logger';
import Api, { FetchParams } from '../../utils/Api';
import i18next from 'i18next';
import { updateSelectedPaymentMethod } from '../../reducers/currentOrder/selectedPaymentMethods';
import { sale } from '../sale';
import getSelectedPaymentMethods from '../../selectors/getSelectedPaymentMethods';
import {
  buildApprovedSubPayment,
  emitEftposStatus,
  getRedcatPaymentFailureReason,
} from './utilities';
import { RedcatPaymentsApiResponse } from './types';

type RedcatPaymentDto = {
  readonly amount: number; 
  readonly idempotencyKey: string
  readonly onPaymentFailed?: () => void
}

export const redcatPayments = createAsyncThunk(
  '$redcatPayments',
  async (
    data: RedcatPaymentDto,
    { dispatch, getState, rejectWithValue },
  ) => {
    const { amount, idempotencyKey, onPaymentFailed } = data;
    try {
      const hook: any = PaymentHooks.get(EFTPOS_STATUS_UPDATE);
      emitEftposStatus(
        hook,
        i18next.t('redcatPaymentsNotification.STARTED'),
        true,
      );

      const params: FetchParams = {
        method: 'POST',
        path: '/api/v2/payments',
        headers: {    
          'X-REDCAT-IDEMPOTENCY-KEY': idempotencyKey,
        },
        body: { amount },
      };

      const response = await Api.fetch<RedcatPaymentsApiResponse>(
        params,
        'trusted',
      );

      if (response.success) {
        // If response.success is true then response.processed_status should always be APPROVED but we are checking it anyway.
        emitEftposStatus(
          hook,
          i18next.t('redcatPaymentsNotification.PROCESSING'),
          true,
        );

        Logger.log(`-----  ----- Redcat Payments TRANSACTION  ----- ----`);
        Logger.log(JSON.stringify(response, null, 2));
        Logger.log(`----- ----- ----- -----  -----  -----  -----`);

        const subPayments = getSelectedPaymentMethods(
          getState() as EntireFrontendState,
        );
        const selectedEftposPayment = subPayments.find(
          subPayment => subPayment.method === PAYMENT_METHOD.EFTPOS,
        );

        if (selectedEftposPayment) {
          dispatch(
            updateSelectedPaymentMethod(
              buildApprovedSubPayment(selectedEftposPayment, response),
            ),
          );
        }

        dispatch(
          sale({
            route: 'checkout',
            authenticationMethod: 'trusted',
            paymentProvider: 'redcat-payments',
            paymentTimestamp: response.payment_timestamp,
          }),
        );
        return;
      }

      throw new Error('Redcat Payments error: unexpected response payload');
    } catch (e) {
      // Log error
      Logger.log(`----- Redcat Payments ERROR -----`);
      Logger.log(JSON.stringify(e, null, 2));
      Logger.log(`----- ----- ----- -----`);

      const reason = await getRedcatPaymentFailureReason(e, {
        onPaymentFailed
      });

      return rejectWithValue({
        e,
        reason: reason || FAILURE_REASON.PAYMENT_FAILED,
      });
    }
  },
);
